import * as React from "react";
import { Box, Typography, Stack } from "@mui/material";
import { useI18n } from "../i18n/I18nProvider";

export default function PrivacyPolicy() {
  const { lang } = useI18n();

  const content = {
    EN: {
      title: "Privacy Policy",
      updated: "Last updated",

      intro:
        "This Privacy Policy explains how takeKONTROL collects, processes, and protects your personal data when you use the platform.",

      sections: [
        {
          title: "1. Data We Collect",
          body: [
            "We collect different types of data depending on how you use the platform. This includes account data, profile data, and behavioral interaction data.",
            "Account data includes your email address and authentication-related information handled via Supabase.",
            "Profile data includes information such as your name, age, role, location, and company affiliation where applicable.",
            "Usage data includes your responses to questionnaires, module interactions, progress tracking, and generated scores such as kontrol score.",
            "We also collect support-related data if you contact us through the platform.",
          ],
        },
        {
          title: "2. Purpose of Processing",
          body: [
            "Your data is processed to provide and operate the platform's core functionality.",
            "This includes generating personalized scores, recommendations, and structured insights based on your inputs.",
            "We also use data to improve the platform, identify patterns, and enhance user experience.",
            "Support data is used solely for responding to inquiries and resolving issues.",
          ],
        },
        {
          title: "3. Profiling and Automated Processing",
          body: [
            "The platform uses automated processing to evaluate user inputs and generate scores and recommendations.",
            "This constitutes profiling, as defined under applicable data protection laws.",
            "These outputs are based on predefined logic and algorithmic models.",
            "They do not produce legal effects and should not be considered as definitive assessments.",
          ],
        },
        {
          title: "4. Organizational Context",
          body: [
            "If you use takeKONTROL as part of an organization, your data may be associated with your employer or organization.",
            "Authorized roles within the organization (e.g., managers or administrators) may access certain data, such as aggregated insights or participation status.",
            "The exact visibility depends on role-based permissions implemented within the platform.",
          ],
        },
        {
          title: "5. Data Storage and Security",
          body: [
            "Your data is stored using Supabase infrastructure, including PostgreSQL databases and authentication services.",
            "We apply security measures such as access controls, encryption in transit, and Row-Level Security (RLS) to ensure that users can only access their own data.",
            "We continuously evaluate and improve our security practices to prevent unauthorized access.",
          ],
        },
        {
          title: "6. Data Sharing",
          body: [
            "We do not sell your personal data.",
            "We share data only with service providers necessary to operate the platform.",
            "This includes Supabase for backend infrastructure and authentication, and Stripe for payment processing where applicable.",
            "These providers process data strictly on our behalf and under contractual obligations.",
          ],
        },
        {
          title: "7. Data Retention",
          body: [
            "We retain your data as long as your account remains active.",
            "Upon deletion of your account, your personal data is deleted or anonymized unless legal obligations require retention.",
            "Certain aggregated or non-identifiable data may be retained for analytical purposes.",
          ],
        },
        {
          title: "8. Your Rights",
          body: [
            "You have the right to access your personal data stored in the platform.",
            "You may update or correct your data at any time through your profile.",
            "You may delete your account, which will trigger data removal processes.",
            "You may request a copy of your data where applicable.",
          ],
        },
        {
          title: "9. Cookies and Technical Tracking",
          body: [
            "We use only minimal technical cookies required for authentication and session management.",
            "No tracking cookies or advertising identifiers are used.",
          ],
        },
        {
          title: "10. Changes to this Policy",
          body: [
            "We may update this Privacy Policy from time to time to reflect changes in the platform or legal requirements.",
            "Continued use of the platform after updates constitutes acceptance of the revised policy.",
          ],
        },
        {
          title: "11. Contact",
          body: [
            "For any questions regarding this Privacy Policy, contact: info@takekontrol.de",
          ],
        },
      ],
    },

    DE: {
      title: "Datenschutzerklärung",
      updated: "Zuletzt aktualisiert",

      intro:
        "Diese Datenschutzerklärung erläutert, wie takeKONTROL personenbezogene Daten erhebt, verarbeitet und schützt.",

      sections: [
        {
          title: "1. Erhobene Daten",
          body: [
            "Wir erheben verschiedene Arten von Daten, abhängig von der Nutzung der Plattform.",
            "Dazu gehören Kontodaten wie E-Mail-Adresse und Authentifizierungsinformationen.",
            "Profildaten umfassen Name, Alter, Position, Standort und Unternehmenszugehörigkeit.",
            "Nutzungsdaten beinhalten Antworten auf Fragebögen, Fortschritte, Interaktionen sowie generierte Scores.",
            "Support-Daten werden erhoben, wenn Sie uns kontaktieren.",
          ],
        },
        {
          title: "2. Zweck der Verarbeitung",
          body: [
            "Die Verarbeitung erfolgt zur Bereitstellung der Plattformfunktionen.",
            "Dazu gehört die Generierung personalisierter Scores und Empfehlungen.",
            "Daten werden auch zur Verbesserung der Plattform genutzt.",
          ],
        },
        {
          title: "3. Profiling und automatisierte Verarbeitung",
          body: [
            "Die Plattform nutzt automatisierte Verfahren zur Erstellung von Bewertungen.",
            "Dies stellt Profiling dar.",
            "Die Ergebnisse haben keine rechtliche Wirkung.",
          ],
        },
        {
          title: "4. Unternehmenskontext",
          body: [
            "Bei Nutzung im Unternehmen können Daten organisatorisch zugeordnet werden.",
            "Autorisierte Rollen können bestimmte Daten einsehen.",
          ],
        },
        {
          title: "5. Speicherung und Sicherheit",
          body: [
            "Die Daten werden über Supabase gespeichert.",
            "Es werden Sicherheitsmaßnahmen wie Zugriffskontrollen und RLS eingesetzt.",
          ],
        },
        {
          title: "6. Datenweitergabe",
          body: [
            "Es erfolgt kein Verkauf personenbezogener Daten.",
            "Weitergabe nur an notwendige Dienstleister (z. B. Supabase, Stripe).",
          ],
        },
        {
          title: "7. Speicherdauer",
          body: [
            "Daten werden gespeichert, solange das Konto aktiv ist.",
            "Nach Löschung werden Daten entfernt oder anonymisiert.",
          ],
        },
        {
          title: "8. Rechte",
          body: [
            "Recht auf Auskunft, Berichtigung und Löschung.",
          ],
        },
        {
          title: "9. Cookies",
          body: [
            "Nur technisch notwendige Cookies werden verwendet.",
          ],
        },
        {
          title: "10. Änderungen",
          body: [
            "Änderungen jederzeit möglich.",
          ],
        },
        {
          title: "11. Kontakt",
          body: ["info@takekontrol.de"],
        },
      ],
    },
  };

  const t = content[lang.toUpperCase() as "EN" | "DE"];

  return (
    <Box maxWidth={900} mx="auto" p={4}>
      <Stack spacing={3}>
        <Typography variant="h4">{t.title}</Typography>

        <Typography variant="body2" color="text.secondary">
          {t.updated}: {new Date().toLocaleDateString()}
        </Typography>

        <Typography>{t.intro}</Typography>

        {t.sections.map((section, i) => (
          <Box key={i}>
            <Typography variant="h6">{section.title}</Typography>
            {section.body.map((line, j) => (
              <Typography key={j}>{line}</Typography>
            ))}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}